// Admin action to grant credits to a school. Mounted on the school resource
// detail/edit views. POSTs to /actions/admin-grant-credits which calls the
// shared AddCredits service — same ledger + balance update path as Stripe
// purchases, but with ledgerType=ADJUSTMENT and the admin's note in the
// description so it's traceable.

import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { axios } from "../config/axios";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";

import {
  Button,
  fetchEnd,
  fetchStart,
  SaveButton,
  useNotify
} from "react-admin";

const CREDIT_TYPES = [
  { value: "EMAIL", label: "Email" },
  { value: "SMS", label: "SMS" },
  { value: "WHATSAPP", label: "WhatsApp" },
  { value: "AI", label: "AI" },
  { value: "TRANSACTION", label: "Transaction (booking commission)" }
];

const QUICK_AMOUNTS = [50, 100, 500, 1000];

export function GrantCreditsButton({ school }) {
  const dispatch = useDispatch();
  const notify = useNotify();

  const [showDialog, setShowDialog] = useState(false);
  const [creditType, setCreditType] = useState("EMAIL");
  const [amount, setAmount] = useState("100");
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  function onOpen() {
    setShowDialog(true);
    setCreditType("EMAIL");
    setAmount("100");
    setNote("");
  }

  function onClose() {
    if (isSubmitting) return;
    setShowDialog(false);
  }

  async function onSubmit() {
    const numAmount = parseInt(amount, 10);
    if (!Number.isInteger(numAmount) || numAmount <= 0) {
      notify("Amount must be a positive integer", "error");
      return;
    }

    dispatch(fetchStart());
    setIsSubmitting(true);
    try {
      const { data } = await axios.post("/actions/admin-grant-credits", {
        schoolID: school._id || school.id,
        creditType,
        amount: numAmount,
        note: note.trim() || undefined
      });
      notify(
        `Granted ${data.data.amountAdded} ${creditType} credits. New balance: ${data.data.newBalance}.`,
        "success"
      );
      setShowDialog(false);
    } catch (e) {
      notify(
        e.response?.data?.message || e.message || "Failed to grant credits",
        "error"
      );
    } finally {
      setIsSubmitting(false);
      dispatch(fetchEnd());
    }
  }

  return (
    <>
      <Button onClick={onOpen} label="Grant credits" />
      <Dialog fullWidth maxWidth="sm" open={showDialog} onClose={onClose}>
        <DialogTitle>Grant credits to {school.name || school._id}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth style={{ marginBottom: 16 }}>
            <InputLabel>Credit type</InputLabel>
            <Select
              value={creditType}
              onChange={e => setCreditType(e.target.value)}
            >
              {CREDIT_TYPES.map(t => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            type="number"
            label="Amount"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            style={{ marginBottom: 8 }}
            inputProps={{ min: 1, max: 100000, step: 1 }}
          />
          <div style={{ marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
            {QUICK_AMOUNTS.map(qa => (
              <Button
                key={qa}
                size="small"
                variant="outlined"
                label={`+${qa}`}
                onClick={() => setAmount(String(qa))}
              />
            ))}
          </div>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Note (shown in the credit ledger)"
            placeholder="e.g. Newsletter beta seed credits"
            value={note}
            onChange={e => setNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <SaveButton
            label="Grant credits"
            saving={isSubmitting}
            disabled={isSubmitting}
            handleSubmitWithRedirect={onSubmit}
            icon={<></>}
          />
          <Button label="Cancel" onClick={onClose} disabled={isSubmitting} />
        </DialogActions>
      </Dialog>
    </>
  );
}
